import type { LucideIcon } from 'lucide-react';
import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: LucideIcon;
  variant?: 'default' | 'ghost' | 'filled' | 'primary';
  size?: 'sm' | 'md' | 'lg';
  active?: boolean;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    { className, variant = 'default', size = 'md', active, icon: Icon, children, ...props },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center rounded-full transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94E1B]/20',
          'disabled:pointer-events-none disabled:opacity-50',
          {
            // Variants
            default: 'text-[#888185] hover:text-[#2E2A23] hover:bg-[#F4F2F2]',
            ghost: 'text-[#888185] hover:text-[#E94E1B]',
            filled: 'bg-[#F4F2F2] text-[#888185] hover:bg-[#CAC7C7]',
            primary: 'bg-[#E94E1B] text-white hover:bg-[#C43D0F]',
          }[variant],
          {
            // Sizes
            sm: 'h-8 w-8',
            md: 'h-10 w-10',
            lg: 'h-12 w-12',
          }[size],
          active && 'text-[#E94E1B] bg-[#FDEAE3]',
          className,
        )}
        {...props}
      >
        {Icon ? (
          <Icon
            className={cn(
              size === 'sm' && 'h-4 w-4',
              size === 'md' && 'h-5 w-5',
              size === 'lg' && 'h-6 w-6',
            )}
          />
        ) : (
          children
        )}
      </button>
    );
  },
);

IconButton.displayName = 'IconButton';

export { IconButton };
